/**
 * Day-column layout for the executive calendar grid.
 * Overlapping events are grouped into clusters and packed into side-by-side
 * columns (Google Calendar style) so titles stay readable.
 */

import { parseISO } from 'date-fns';
import type { DelegateCalendarEventRow } from './executiveCalendarDelegatedService';

export type DayEventLayout = {
  topPct: number;
  heightPct: number;
  leftPct: number;
  widthPct: number;
  clusterId: number;
  /** Pixel-independent hint: true when the pill is under ~25 minutes tall. */
  isShort: boolean;
};

export type DayEventCluster = {
  clusterId: number;
  startMs: number;
  endMs: number;
  events: DelegateCalendarEventRow[];
};

export type DayLayoutResult = {
  /** Keyed by `${calendarId}:${eventId}`. */
  layouts: Map<string, DayEventLayout>;
  clusters: DayEventCluster[];
};

export function dayLayoutEventKey(ev: DelegateCalendarEventRow): string {
  return `${ev.calendarId}:${ev.id}`;
}

type Positioned = {
  ev: DelegateCalendarEventRow;
  startMin: number;
  endMin: number;
  startMs: number;
  endMs: number;
  column: number;
};

const SHORT_EVENT_MINUTES = 25;

function clipToDay(
  ev: DelegateCalendarEventRow,
  dayStartMin: number,
  dayEndMin: number,
): { startMin: number; endMin: number; startMs: number; endMs: number } | null {
  if (!ev.start || !ev.end || ev.isAllDay) return null;
  let start: Date;
  let end: Date;
  try {
    start = parseISO(ev.start);
    end = parseISO(ev.end);
  } catch {
    return null;
  }
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes();
  const clipStart = Math.max(startMin, dayStartMin);
  const clipEnd = Math.min(endMin, dayEndMin);
  if (clipEnd <= clipStart) return null;
  return { startMin: clipStart, endMin: clipEnd, startMs: start.getTime(), endMs: end.getTime() };
}

/**
 * Compute per-event geometry (percentages of the day body) plus overlap
 * clusters for one day's events.
 */
export function layoutDayEvents(
  events: DelegateCalendarEventRow[],
  dayStartHour: number,
  dayEndHour: number,
): DayLayoutResult {
  const dayStartMin = dayStartHour * 60;
  const dayEndMin = dayEndHour * 60;
  const minutesVisible = dayEndMin - dayStartMin;

  const positioned: Positioned[] = [];
  for (const ev of events) {
    const clipped = clipToDay(ev, dayStartMin, dayEndMin);
    if (!clipped) continue;
    positioned.push({ ev, ...clipped, column: 0 });
  }

  // Earlier start first; for ties the longer event first so it takes column 0.
  positioned.sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  const layouts = new Map<string, DayEventLayout>();
  const clusters: DayEventCluster[] = [];

  let i = 0;
  while (i < positioned.length) {
    // Grow a cluster of transitively-overlapping events.
    let clusterEnd = positioned[i].endMin;
    let j = i + 1;
    while (j < positioned.length && positioned[j].startMin < clusterEnd) {
      clusterEnd = Math.max(clusterEnd, positioned[j].endMin);
      j++;
    }
    const cluster = positioned.slice(i, j);
    const clusterId = clusters.length;

    // First-fit column assignment within the cluster.
    const columnEnds: number[] = [];
    for (const item of cluster) {
      let col = columnEnds.findIndex((end) => end <= item.startMin);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(item.endMin);
      } else {
        columnEnds[col] = item.endMin;
      }
      item.column = col;
    }

    const columnCount = columnEnds.length;
    const widthPct = 100 / columnCount;
    for (const item of cluster) {
      const top = ((item.startMin - dayStartMin) / minutesVisible) * 100;
      const height = ((item.endMin - item.startMin) / minutesVisible) * 100;
      layouts.set(dayLayoutEventKey(item.ev), {
        topPct: top,
        heightPct: Math.max(height, 1.2),
        leftPct: item.column * widthPct,
        widthPct,
        clusterId,
        isShort: item.endMin - item.startMin < SHORT_EVENT_MINUTES,
      });
    }

    clusters.push({
      clusterId,
      startMs: Math.min(...cluster.map((c) => c.startMs)),
      endMs: Math.max(...cluster.map((c) => c.endMs)),
      events: cluster
        .slice()
        .sort((a, b) => a.startMs - b.startMs)
        .map((c) => c.ev),
    });

    i = j;
  }

  return { layouts, clusters };
}
